import { useMemo, useState, type FormEvent } from 'react'
import { Button } from '@/ui/Button'
import type { WorkflowField, WorkflowPublic } from '@/lib/api-types'

export type FormValues = Record<string, string | number | boolean>

interface Props {
  workflow: WorkflowPublic
  code: string
  busy: boolean
  onSubmit: (values: FormValues) => void
  onCancel: () => void
}

function initialValues(fields: readonly WorkflowField[]): FormValues {
  const values: FormValues = {}
  for (const field of fields) {
    if (field.type === 'boolean') values[field.label] = false
    else if (field.defaultValue !== null && field.defaultValue !== '') {
      values[field.label] = field.defaultValue
    }
  }
  return values
}

/**
 * Field entry for workflows that don't submit immediately.
 *
 * Validation reports every problem at once and moves focus to the first one.
 * The old app returned on the first failure with a toast naming a single field,
 * so fixing a form with two omissions meant two round trips.
 */
export function ScanForm({ workflow, code, busy, onSubmit, onCancel }: Props) {
  const [values, setValues] = useState<FormValues>(() => initialValues(workflow.fields))
  const [errors, setErrors] = useState<Record<string, string>>({})

  const fields = useMemo(
    () => [...workflow.fields].sort((a, b) => a.position - b.position),
    [workflow.fields],
  )

  const setValue = (label: string, value: string | number | boolean): void => {
    setValues((current) => ({ ...current, [label]: value }))
    setErrors((current) => {
      if (current[label] === undefined) return current
      const next = { ...current }
      delete next[label]
      return next
    })
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()

    const found: Record<string, string> = {}
    for (const field of fields) {
      if (!field.required) continue
      const value = values[field.label]
      if (field.type === 'boolean') {
        if (value !== true) found[field.label] = 'This needs to be ticked.'
      } else if (value === undefined || String(value).trim() === '') {
        found[field.label] = 'This is required.'
      }
    }

    if (Object.keys(found).length > 0) {
      setErrors(found)
      const first = fields.find((f) => found[f.label] !== undefined)
      if (first !== undefined) {
        document.getElementById(`field-${first.id}`)?.focus()
      }
      return
    }

    onSubmit(values)
  }

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-[var(--surface-app)]">
      <form onSubmit={submit} className="inset-safe mx-auto w-full max-w-md px-5 py-6">
        <h1 className="text-2xl font-bold tracking-tight">{workflow.name}</h1>

        <div className="mt-4 rounded-2xl bg-[var(--surface-sunken)] p-4">
          <p className="text-sm font-semibold text-[var(--text-muted)]">Scanned code</p>
          <p className="mt-1 break-all font-mono text-[0.9375rem]">{code}</p>
        </div>

        <div className="mt-6 space-y-5">
          {fields.map((field) => {
            const error = errors[field.label]
            const describedBy = error !== undefined ? `error-${field.id}` : undefined

            return (
              <div key={field.id} className="space-y-2">
                <label
                  htmlFor={`field-${field.id}`}
                  className="block text-[0.9375rem] font-semibold"
                >
                  {field.label}
                  {field.required && (
                    <span className="ml-1 text-bad-600" aria-label="required">
                      *
                    </span>
                  )}
                </label>

                {field.type === 'select' ? (
                  <select
                    id={`field-${field.id}`}
                    value={String(values[field.label] ?? '')}
                    onChange={(e) => setValue(field.label, e.target.value)}
                    aria-invalid={error !== undefined}
                    aria-describedby={describedBy}
                    className="min-h-touch w-full rounded-2xl border border-[var(--border-input)] bg-[var(--surface-raised)] px-4 outline-none focus:border-brand-600"
                  >
                    <option value="">Choose…</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'boolean' ? (
                  <label className="flex min-h-touch cursor-pointer items-center justify-between gap-3 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-4">
                    <span className="text-[0.9375rem]">Yes</span>
                    <input
                      id={`field-${field.id}`}
                      type="checkbox"
                      checked={values[field.label] === true}
                      onChange={(e) => setValue(field.label, e.target.checked)}
                      aria-invalid={error !== undefined}
                      aria-describedby={describedBy}
                      className="h-6 w-6 accent-brand-600"
                    />
                  </label>
                ) : (
                  <input
                    id={`field-${field.id}`}
                    type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                    inputMode={field.type === 'number' ? 'decimal' : undefined}
                    value={String(values[field.label] ?? '')}
                    onChange={(e) =>
                      setValue(
                        field.label,
                        field.type === 'number' && e.target.value !== ''
                          ? Number(e.target.value)
                          : e.target.value,
                      )
                    }
                    aria-invalid={error !== undefined}
                    aria-describedby={describedBy}
                    className="min-h-touch w-full rounded-2xl border border-[var(--border-input)] bg-[var(--surface-raised)] px-4 outline-none focus:border-brand-600"
                  />
                )}

                {error !== undefined && (
                  <p id={`error-${field.id}`} role="alert" className="text-sm text-bad-700">
                    {error}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Button type="submit" size="lg" fullWidth busy={busy}>
            Send scan
          </Button>
          <Button type="button" variant="ghost" fullWidth onClick={onCancel} disabled={busy}>
            Discard
          </Button>
        </div>
      </form>
    </div>
  )
}
